import * as React from "react";
import { UserContext } from "../context/UserContext";
import {
  AppBar,
  Toolbar,
  MenuItem,
  Menu,
  Tabs,
  Tab,
  Theme,
  IconButton,
} from "@material-ui/core";
import { withRouter } from "react-router-dom";
import { makeStyles, createStyles } from "@material-ui/styles";
import { RouterProps, useLocation } from "react-router";
import { Routes } from "../constants/Routes";
import SettingsIcon from "@material-ui/icons/SettingsOutlined";
import PhoneIcon from "@material-ui/icons/PhoneOutlined";
import MoreIcon from "@material-ui/icons/MoreVert";
import UserAvatar from "./UserAvatar";
import { CompanyContext } from "../context/CompanyContext";
import { Api } from "../api";
import { hasError } from "../helpers/ResponseHelper";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      zIndex: 1200,
    },
    companyButton: {
      marginRight: 10,
      marginTop: 2,
    },
    bigIndicator: {
      height: 5,
    },
    title: {
      fontSize: 20,
      fontWeight: 700,
      marginRight: 16,
      color: theme.palette.secondary.main,
      [theme.breakpoints.down("xs")]: {
        display: "none",
      },
    },
    toolbarLink: {
      textDecoration: "none",
      color: "black",
    },
    tabs: {
      flexGrow: 1,
    },
    button: {
      color: theme.palette.secondary.main,
    },
    leftButtons: {
      flexGrow: 1,
    },
    avatar: {
      background: "#20A6FC",
      fontSize: 14,
      float: "right",
      height: 38,
      width: 38,
    },
    tab: {
      minHeight: 64,
      fontSize: 14,
    },
    iconButton: {
      float: "right",
      marginRight: 15,
      border: "2px solid gray",
      height: 40,
      width: 40,
      padding: 5,
      [theme.breakpoints.down("xs")]: {
        height: 38,
        width: 38,
        padding: 0,
        marginRight: 12,
      },
    },
    icon: {
      fontSize: 18,
    },
    avatarButton: {
      float: "right",
      marginRight: 10,
      height: 40,
      width: 40,
      padding: 0,
    },
    sectionDesktop: {
      display: "none",
      [theme.breakpoints.up("md")]: {
        display: "flex",
      },
    },
    sectionMobile: {
      display: "flex",
      [theme.breakpoints.up("md")]: {
        display: "none",
      },
    },
    offset: {
      ...theme.mixins.toolbar,
      flexGrow: 1,
    },
    hide: {
      display: "none",
    },
    customLogo: {
      position: "relative",
      display: "inline-block",
      width: 400,
      height: 60,
      backgroundRepeat: "no-repeat",
      backgroundSize: "contain",
    },
  })
);

function AppToolbar(props: RouterProps) {
  const [hideTabSelection, setHideTabSelection] = React.useState(true);
  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const user = React.useContext(UserContext);
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [
    mobileMoreAnchorEl,
    setMobileMoreAnchorEl,
  ] = React.useState<null | HTMLElement>(null);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const location = useLocation();
  const companyContext = React.useContext(CompanyContext);
  const [logoPath, setLogoPath] = React.useState("");

  React.useEffect(() => {
    setHideTabSelection(false);
    switch (location.pathname) {
      case Routes.dashboard:
        setSelectedTabIndex(0);
        break;
      case Routes.resourceLibrary:
        setSelectedTabIndex(1);
        break;
      case Routes.reports:
        setSelectedTabIndex(2);
        break;
      default:
        setHideTabSelection(true);
        break;
    }
  }, [location.pathname]);

  React.useEffect(() => {
    if (companyContext.companyId) {
      Api.companies.getOne(companyContext.companyId).then((res) => {
        if (!hasError(res)) {
          setLogoPath(res.data.data.logoPath ?? "none");
        }
      });
    }
  }, [companyContext.companyId]);

  function handleAvatarClick(event: React.MouseEvent<HTMLElement>) {
    props.history.push(Routes.profile);
  }

  function handleAvatarClose() {
    setAnchorEl(null);
  }

  function handleChange(event: React.ChangeEvent<{}>, newValue: number) {
    setSelectedTabIndex(newValue);
  }

  function goToAdmin() {
    props.history.push(Routes.adminEmployees);
    setHideTabSelection(true);
    handleMobileMenuClose();
  }

  function handleMobileMenuClose() {
    setMobileMoreAnchorEl(null);
  }

  function handleMobileMenuOpen(event: React.MouseEvent<HTMLElement>) {
    setMobileMoreAnchorEl(event.currentTarget);
  }

  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      id="mobile-menu"
      keepMounted
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MenuItem
        onClick={() => {
          props.history.push("/company-contacts");
          handleMobileMenuClose();
        }}
      >
        <IconButton className={classes.iconButton}>
          <PhoneIcon className={classes.icon} />
        </IconButton>
        <p>Contact Us</p>
      </MenuItem>
      {user.attributes.companyAdmin || user.attributes.clerical ? (
        <MenuItem onClick={goToAdmin}>
          <IconButton className={classes.iconButton}>
            <SettingsIcon className={classes.icon} />
          </IconButton>
          <p>Admin</p>
        </MenuItem>
      ) : null}
      <MenuItem onClick={handleAvatarClick}>
        <IconButton className={classes.avatarButton}>
          <UserAvatar
            firstName={user.attributes.firstName || ""}
            lastName={user.attributes.lastName || ""}
          />
        </IconButton>
        <p>Profile</p>
      </MenuItem>
    </Menu>
  );

  return (
    <React.Fragment>
      {user.authenticated && !user.tosRequired ? (
        <>
          <AppBar position="fixed" color="default" className={classes.appBar}>
            <Toolbar>
              {logoPath ? (
                <>
                  {logoPath === "none" ? (
                    <div className="logo" />
                  ) : (
                    <img
                      style={{ height: 60 }}
                      src={logoPath}
                      alt="Company Logo"
                    />
                  )}
                </>
              ) : null}
              {user.profile.email_verified ? (
                <Tabs
                  className={classes.tabs}
                  classes={{
                    indicator: classes.bigIndicator,
                  }}
                  value={hideTabSelection ? false : selectedTabIndex}
                  onChange={handleChange}
                  indicatorColor="secondary"
                >
                  <Tab
                    label="Dashboard"
                    className={classes.tab}
                    onClick={() => props.history.push(Routes.dashboard)}
                  />
                  <Tab
                    label="Resource Library"
                    className={classes.tab}
                    onClick={() => props.history.push(Routes.resourceLibrary)}
                  />
                  {user.attributes.companyAdmin ||
                  user.attributes.hr ||
                  user.attributes.clerical ? (
                    <Tab
                      label="Reports"
                      className={classes.tab}
                      onClick={() => props.history.push(Routes.reports)}
                    />
                  ) : null}
                </Tabs>
              ) : (
                <div className={classes.tabs} />
              )}

              <div className={classes.sectionDesktop}>
                <IconButton
                  className={classes.iconButton}
                  onClick={() => props.history.push("/company-contacts")}
                >
                  <PhoneIcon />
                </IconButton>
                {user.attributes.companyAdmin ||
                user.attributes.hr ||
                user.attributes.clerical ? (
                  <IconButton
                    className={classes.iconButton}
                    onClick={goToAdmin}
                  >
                    <SettingsIcon />
                  </IconButton>
                ) : null}
                <IconButton
                  className={classes.avatarButton}
                  onClick={handleAvatarClick}
                >
                  <UserAvatar
                    firstName={user.attributes.firstName}
                    lastName={user.attributes.lastName}
                  />
                </IconButton>
                <Menu
                  id="simple-menu"
                  anchorEl={anchorEl}
                  keepMounted
                  open={Boolean(anchorEl)}
                  onClose={handleAvatarClose}
                >
                  <MenuItem onClick={user.logout}>Logout</MenuItem>
                </Menu>
              </div>

              <div className={classes.sectionMobile}>
                <IconButton
                  aria-label="show more"
                  aria-controls="mobile-menu"
                  aria-haspopup="true"
                  onClick={handleMobileMenuOpen}
                  color="inherit"
                >
                  <MoreIcon />
                </IconButton>
              </div>
            </Toolbar>
          </AppBar>
          {renderMobileMenu}
          <div className={classes.offset} />
        </>
      ) : null}
    </React.Fragment>
  );
}

export default withRouter(AppToolbar);
